import {
  ArrowDownToLine,
  Coffee,
  Disc3,
  Heart,
  Library,
  ListMusic,
  Loader2,
  LogOut,
  Music2,
  Pause,
  Play,
  Plus,
  Radio,
  Search,
  Settings,
  ShieldCheck,
  SkipForward,
  SlidersHorizontal,
  Sparkles,
  User,
  Waves
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

type ViewKey = "stations" | "library" | "favorites" | "account";

type ByteBistroUser = {
  userId: string;
  displayName: string;
  email: string;
  plan: string;
  isGuest: boolean;
  joinedAt: string;
};

type Track = {
  trackId: string;
  title: string;
  prompt: string;
  style: string;
  styleLabel: string;
  mood: string;
  durationSeconds: number;
  bpm: number;
  key: string;
  instruments: string[];
  createdAt: string;
  status: "ready" | "queued" | "rendering" | "failed";
  favorite: boolean;
  audioUrl: string;
  downloadUrl: string;
  model: string;
  arrangement?: StationArrangement;
};

type Playlist = {
  playlistId: string;
  name: string;
  description: string;
  trackIds: string[];
  createdAt: string;
  updatedAt: string;
};

type GeneratorSettings = {
  style: string;
  mood: string;
  durationSeconds: number;
  bpm: number;
  key: string;
  instruments: string[];
  vocals: false;
};

type ArrangementSettings = Omit<GeneratorSettings, "vocals"> & {
  vocals?: false;
};

type StationArrangement = {
  title?: string;
  prompt?: string;
  sourcePrompt?: string;
  settings?: ArrangementSettings;
  arranger?: string;
};

type ApiResponse<T> = T & {
  ok: boolean;
  error?: string;
  detail?: string;
};

type Station = {
  id: string;
  title: string;
  genre: string;
  vibe: string;
  prompt: string;
  settings: GeneratorSettings;
  tags: string[];
  accent: "teal" | "coral" | "gold" | "blue";
};

type UserSettings = {
  defaultStationId: string;
  defaultDurationSeconds: number;
  continuousStation: boolean;
  autoPlayOnTune: boolean;
  downloadQuality: "standard" | "high";
  emailUpdates: boolean;
};

const API_ROOT = "/api/bytebistro";
const USER_STORAGE_KEY = "bytebistro.user";
const PROMPT_STATION_ID = "prompt-station";

const defaultUserSettings: UserSettings = {
  defaultStationId: "lofi-study",
  defaultDurationSeconds: 180,
  continuousStation: true,
  autoPlayOnTune: true,
  downloadQuality: "standard",
  emailUpdates: false
};

const stations: Station[] = [
  {
    id: "lofi-study",
    title: "LoFi Study",
    genre: "lofi",
    vibe: "Warm keys, dusty drums, low-pressure focus.",
    prompt: "lofi study background music with warm electric piano, mellow bass, and soft vinyl texture",
    settings: {
      style: "lofi",
      mood: "warm",
      durationSeconds: 180,
      bpm: 74,
      key: "C",
      instruments: ["electric_piano", "upright_bass", "soft_drums"],
      vocals: false
    },
    tags: ["study", "soft drums", "warm"],
    accent: "teal"
  },
  {
    id: "jazz-cafe",
    title: "Jazz Cafe",
    genre: "jazz",
    vibe: "Small-table piano, upright bass, late coffee.",
    prompt: "jazz coffee shop instrumental with relaxed piano chords, upright bass, and brushed drums",
    settings: {
      style: "jazz",
      mood: "bright",
      durationSeconds: 180,
      bpm: 112,
      key: "F",
      instruments: ["piano", "upright_bass", "soft_drums"],
      vocals: false
    },
    tags: ["coffee", "piano", "swing"],
    accent: "coral"
  },
  {
    id: "coding-flow",
    title: "Coding Flow",
    genre: "focus",
    vibe: "Steady pulses that stay out of the way.",
    prompt: "deep focus coding background music with synth pad, mallets, soft drums, and a steady pulse",
    settings: {
      style: "focus",
      mood: "steady",
      durationSeconds: 180,
      bpm: 92,
      key: "D",
      instruments: ["synth_pad", "mallets", "soft_drums"],
      vocals: false
    },
    tags: ["coding", "focus", "steady"],
    accent: "blue"
  },
  {
    id: "ambient-sleep",
    title: "Ambient Sleep",
    genre: "ambient",
    vibe: "Slow pads, soft edges, no surprises.",
    prompt: "ambient sleep instrumental with slow synth pads, gentle strings, and floating piano notes",
    settings: {
      style: "ambient",
      mood: "calm",
      durationSeconds: 180,
      bpm: 58,
      key: "A",
      instruments: ["synth_pad", "strings", "piano"],
      vocals: false
    },
    tags: ["sleep", "pads", "calm"],
    accent: "teal"
  },
  {
    id: "piano-rain",
    title: "Piano Rain",
    genre: "piano",
    vibe: "Minimal piano loops for writing and quiet rooms.",
    prompt: "gentle piano soundscape with soft sustained strings and a rainy evening mood",
    settings: {
      style: "piano",
      mood: "gentle",
      durationSeconds: 180,
      bpm: 72,
      key: "C",
      instruments: ["piano", "strings"],
      vocals: false
    },
    tags: ["piano", "writing", "soft"],
    accent: "gold"
  },
  {
    id: "acoustic-morning",
    title: "Acoustic Morning",
    genre: "acoustic",
    vibe: "Clean guitar patterns and a relaxed pulse.",
    prompt: "acoustic guitar instrumental with organic fingerpicked rhythm, mellow bass, and light mallets",
    settings: {
      style: "acoustic",
      mood: "organic",
      durationSeconds: 180,
      bpm: 86,
      key: "G",
      instruments: ["acoustic_guitar", "upright_bass", "mallets"],
      vocals: false
    },
    tags: ["guitar", "morning", "organic"],
    accent: "coral"
  }
];

const moodOptions = ["calm", "warm", "bright", "steady", "organic", "gentle", "wide", "uplifting"];
const keyOptions = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const instrumentOptions = [
  { value: "piano", label: "Piano" },
  { value: "electric_piano", label: "Electric piano" },
  { value: "synth_pad", label: "Synth pad" },
  { value: "soft_drums", label: "Soft drums" },
  { value: "upright_bass", label: "Upright bass" },
  { value: "acoustic_guitar", label: "Acoustic guitar" },
  { value: "mallets", label: "Mallets" },
  { value: "strings", label: "Strings" }
];

const defaultStation = stations[0];
const stationTwists = [
  "with a softer intro and extra room around the chords",
  "with a deeper low end and a slightly wider stereo feel",
  "with a brighter top line and gentle movement in the background",
  "with a more minimal arrangement and relaxed transitions",
  "with a warmer middle section and subtle late-night texture",
  "with a calmer groove and a slow-building instrumental arc"
];

const bpmOffsets = [0, 2, -2, 4, -3, 1];
const keyOffsets = [0, 5, 9, 2, 7, 11];

function getStoredUser(): ByteBistroUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ByteBistroUser) : null;
  } catch {
    return null;
  }
}

function storeUser(user: ByteBistroUser) {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearStoredUser() {
  localStorage.removeItem(USER_STORAGE_KEY);
}

function fullUrl(path: string) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return path;
}

function formatDuration(seconds: number) {
  const safeSeconds = Math.max(0, Math.round(seconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = String(safeSeconds % 60).padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function formatInstrumentLabel(value: string) {
  return instrumentOptions.find((instrument) => instrument.value === value)?.label || value.replace(/_/g, " ");
}

function nextStationVariation(station: Station, round: number, durationSeconds = station.settings.durationSeconds) {
  const twist = stationTwists[round % stationTwists.length];
  const bpm = Math.max(50, Math.min(180, station.settings.bpm + bpmOffsets[round % bpmOffsets.length]));
  const keyIndex = keyOptions.indexOf(station.settings.key);
  const key = keyOptions[(Math.max(0, keyIndex) + keyOffsets[round % keyOffsets.length]) % keyOptions.length];
  return {
    prompt: `${station.title} stream ${round + 1}: ${station.prompt}, ${twist}.`,
    settings: {
      ...station.settings,
      durationSeconds,
      bpm,
      key
    }
  };
}

function nextPromptStationVariation(sourcePrompt: string, round: number) {
  const twist = stationTwists[round % stationTwists.length];
  return `${sourcePrompt}, ${twist}`;
}

async function api<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const storedUser = getStoredUser();
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  if (storedUser?.userId) {
    headers.set("X-ByteBistro-User", storedUser.userId);
  }
  const response = await fetch(`${API_ROOT}${path}`, {
    ...options,
    credentials: "include",
    headers
  });
  const payload = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error || payload.detail || `ByteBistro API failed (${response.status})`);
  }
  return payload;
}

function App() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const stationRoundsRef = useRef<Record<string, number>>({});
  const queueingRef = useRef(false);
  const [activeView, setActiveView] = useState<ViewKey>("stations");
  const [user, setUser] = useState<ByteBistroUser | null>(() => getStoredUser());
  const [prompt, setPrompt] = useState(defaultStation.prompt);
  const [settings, setSettings] = useState<GeneratorSettings>(defaultStation.settings);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [playlistName, setPlaylistName] = useState("Late night focus");
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("");
  const [currentTrackId, setCurrentTrackId] = useState("");
  const [activeStationId, setActiveStationId] = useState(defaultStation.id);
  const [query, setQuery] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isQueueing, setIsQueueing] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [continuousMode, setContinuousMode] = useState(true);
  const [queuedTrack, setQueuedTrack] = useState<Track | null>(null);
  const [promptStationSource, setPromptStationSource] = useState("");
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [accountDraft, setAccountDraft] = useState({ name: "The Byte Bistro Listener", email: "", password: "" });
  const [userSettings, setUserSettings] = useState<UserSettings>(defaultUserSettings);
  const [settingsDraft, setSettingsDraft] = useState<UserSettings>(defaultUserSettings);

  useEffect(() => {
    let mounted = true;
    async function boot() {
      try {
        let activeUser = getStoredUser();
        if (!activeUser) {
          const auth = await api<{ user: ByteBistroUser }>("/auth/demo", {
            method: "POST",
            body: JSON.stringify({ name: "The Byte Bistro Listener" })
          });
          activeUser = auth.user;
          storeUser(activeUser);
        }
        if (!mounted) return;
        setUser(activeUser);
        setAccountDraft({ name: activeUser.displayName, email: activeUser.email, password: "" });
        await Promise.all([loadTracks(), loadPlaylists(), loadUserSettings(true)]);
      } catch (bootError) {
        if (!mounted) return;
        setError(bootError instanceof Error ? bootError.message : "ByteBistro could not connect.");
      } finally {
        if (mounted) setIsLoading(false);
      }
    }
    boot();
    return () => {
      mounted = false;
    };
  }, []);

  const currentTrack = useMemo(
    () => tracks.find((track) => track.trackId === currentTrackId) || tracks[0],
    [currentTrackId, tracks]
  );

  const favoriteTracks = useMemo(() => tracks.filter((track) => track.favorite), [tracks]);
  const visibleTracks = useMemo(() => {
    const source = activeView === "favorites" ? favoriteTracks : tracks;
    const normalized = query.trim().toLowerCase();
    if (!normalized) return source;
    return source.filter((track) =>
      [track.title, track.prompt, track.styleLabel, track.mood, track.key].some((value) =>
        value.toLowerCase().includes(normalized)
      )
    );
  }, [activeView, favoriteTracks, query, tracks]);

  const selectedPlaylist = playlists.find((playlist) => playlist.playlistId === selectedPlaylistId);
  const activeStation = stations.find((station) => station.id === activeStationId) || defaultStation;
  const arrangedTrack = currentTrack?.arrangement?.arranger ? currentTrack : null;

  useEffect(() => {
    if (!currentTrack || !shouldAutoplay || !audioRef.current) return;
    const audio = audioRef.current;
    audio.load();
    const playRequest = audio.play();
    if (playRequest) {
      playRequest.catch(() => {
        setNotice("Press play once and the station will keep going from there.");
      });
    }
  }, [currentTrack?.trackId, shouldAutoplay]);

  async function loadTracks(resetSelection = false) {
    const payload = await api<{ tracks: Track[] }>("/tracks");
    setTracks(payload.tracks);
    setCurrentTrackId((existing) => (resetSelection ? payload.tracks[0]?.trackId || "" : existing || payload.tracks[0]?.trackId || ""));
  }

  async function loadPlaylists(resetSelection = false) {
    const payload = await api<{ playlists: Playlist[] }>("/playlists");
    setPlaylists(payload.playlists);
    setSelectedPlaylistId((existing) =>
      resetSelection ? payload.playlists[0]?.playlistId || "" : existing || payload.playlists[0]?.playlistId || ""
    );
  }

  async function loadUserSettings(applyToMixer = false) {
    const payload = await api<{ settings: UserSettings }>("/settings");
    setUserSettings(payload.settings);
    setSettingsDraft(payload.settings);
    setContinuousMode(payload.settings.continuousStation);
    if (applyToMixer) {
      const defaultTune = stations.find((station) => station.id === payload.settings.defaultStationId) || defaultStation;
      setActiveStationId(defaultTune.id);
      setPromptStationSource("");
      setPrompt(defaultTune.prompt);
      setSettings({ ...defaultTune.settings, durationSeconds: payload.settings.defaultDurationSeconds });
    }
    return payload.settings;
  }

  function applySavedSettings(nextSettings: UserSettings) {
    setUserSettings(nextSettings);
    setSettingsDraft(nextSettings);
    setContinuousMode(nextSettings.continuousStation);
    const defaultTune = stations.find((station) => station.id === nextSettings.defaultStationId) || defaultStation;
    setActiveStationId(defaultTune.id);
    setPromptStationSource("");
    setPrompt(defaultTune.prompt);
    setSettings({ ...defaultTune.settings, durationSeconds: nextSettings.defaultDurationSeconds });
  }

  async function createTrack(nextPrompt: string, nextSettings: GeneratorSettings) {
    const payload = await api<{ track: Track }>("/generate", {
      method: "POST",
      body: JSON.stringify({ prompt: nextPrompt, settings: nextSettings })
    });
    setTracks((items) => [payload.track, ...items.filter((item) => item.trackId !== payload.track.trackId)]);
    return payload.track;
  }

  async function createArrangedTrack(nextPrompt: string) {
    const payload = await api<{ track: Track }>("/generate", {
      method: "POST",
      body: JSON.stringify({
        prompt: nextPrompt,
        autoArrange: true,
        durationSeconds: userSettings.defaultDurationSeconds
      })
    });
    setTracks((items) => [payload.track, ...items.filter((item) => item.trackId !== payload.track.trackId)]);
    return payload.track;
  }

  function nextRound(stationId: string) {
    const current = stationRoundsRef.current[stationId] || 0;
    stationRoundsRef.current[stationId] = current + 1;
    return current;
  }

  async function generateTrack(nextPrompt: string, nextSettings: GeneratorSettings, stationId?: string) {
    setIsGenerating(true);
    setError("");
    setNotice("");
    try {
      const track = await createTrack(nextPrompt, nextSettings);
      setCurrentTrackId(track.trackId);
      if (stationId) setActiveStationId(stationId);
      setNotice(stationId ? "Station is ready." : "Custom instrumental is ready.");
      return track;
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "Track generation failed.");
      return null;
    } finally {
      setIsGenerating(false);
    }
  }

  async function generatePromptStationTrack(nextPrompt: string) {
    setIsGenerating(true);
    setError("");
    setNotice("");
    try {
      const track = await createArrangedTrack(nextPrompt);
      setCurrentTrackId(track.trackId);
      setActiveStationId(PROMPT_STATION_ID);
      setSettings({
        style: track.style,
        mood: track.mood,
        durationSeconds: track.durationSeconds,
        bpm: track.bpm,
        key: track.key,
        instruments: track.instruments,
        vocals: false
      });
      setNotice(`${track.title} is ready.`);
      return track;
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "Station generation failed.");
      return null;
    } finally {
      setIsGenerating(false);
    }
  }

  async function queueNextStationTrack(station: Station, force = false) {
    if ((!continuousMode && !force) || queueingRef.current) return;
    queueingRef.current = true;
    setIsQueueing(true);
    try {
      const variation = nextStationVariation(station, nextRound(station.id), userSettings.defaultDurationSeconds);
      const track = await createTrack(variation.prompt, variation.settings);
      setQueuedTrack(track);
    } catch (queueError) {
      setError(queueError instanceof Error ? queueError.message : "Could not queue the next station track.");
    } finally {
      queueingRef.current = false;
      setIsQueueing(false);
    }
  }

  async function queueNextPromptStationTrack(sourcePrompt: string, force = false) {
    if ((!continuousMode && !force) || queueingRef.current || !sourcePrompt.trim()) return;
    queueingRef.current = true;
    setIsQueueing(true);
    try {
      const variationPrompt = nextPromptStationVariation(sourcePrompt, nextRound(PROMPT_STATION_ID));
      const track = await createArrangedTrack(variationPrompt);
      setQueuedTrack(track);
    } catch (queueError) {
      setError(queueError instanceof Error ? queueError.message : "Could not queue the next prompt station track.");
    } finally {
      queueingRef.current = false;
      setIsQueueing(false);
    }
  }

  async function tuneStation(station: Station) {
    const firstVariation = nextStationVariation(station, nextRound(station.id), userSettings.defaultDurationSeconds);
    const shouldContinue = userSettings.continuousStation;
    setPromptStationSource("");
    setPrompt(firstVariation.prompt);
    setSettings(firstVariation.settings);
    setActiveStationId(station.id);
    setQueuedTrack(null);
    setContinuousMode(shouldContinue);
    setShouldAutoplay(userSettings.autoPlayOnTune);
    const track = await generateTrack(firstVariation.prompt, firstVariation.settings, station.id);
    if (track && shouldContinue) {
      void queueNextStationTrack(station, true);
    }
  }

  async function generateCustom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const sourcePrompt = prompt.trim();
    if (sourcePrompt.length < 4) return;
    const shouldContinue = true;
    stationRoundsRef.current[PROMPT_STATION_ID] = 0;
    setPrompt(sourcePrompt);
    setPromptStationSource(sourcePrompt);
    setActiveStationId(PROMPT_STATION_ID);
    setContinuousMode(shouldContinue);
    setQueuedTrack(null);
    setShouldAutoplay(userSettings.autoPlayOnTune);
    setIsGenerating(true);
    setError("");
    setNotice("");
    try {
      const track = await createArrangedTrack(sourcePrompt);
      setCurrentTrackId(track.trackId);
      setSettings({
        style: track.style,
        mood: track.mood,
        durationSeconds: track.durationSeconds,
        bpm: track.bpm,
        key: track.key,
        instruments: track.instruments,
        vocals: false
      });
      setNotice(`${track.title} is ready.`);
      if (shouldContinue) {
        void queueNextPromptStationTrack(sourcePrompt, true);
      }
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : "Station generation failed.");
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleTrackEnded() {
    setIsAudioPlaying(false);
    if (!continuousMode) return;
    setShouldAutoplay(true);
    if (queuedTrack) {
      const nextTrack = queuedTrack;
      setQueuedTrack(null);
      setCurrentTrackId(nextTrack.trackId);
      if (activeStationId === PROMPT_STATION_ID) {
        void queueNextPromptStationTrack(promptStationSource || nextTrack.arrangement?.sourcePrompt || prompt);
        return;
      }
      void queueNextStationTrack(activeStation);
      return;
    }
    if (activeStationId === PROMPT_STATION_ID) {
      const sourcePrompt = promptStationSource || currentTrack?.arrangement?.sourcePrompt || prompt;
      const variationPrompt = nextPromptStationVariation(sourcePrompt, nextRound(PROMPT_STATION_ID));
      const track = await generatePromptStationTrack(variationPrompt);
      if (track) {
        void queueNextPromptStationTrack(sourcePrompt);
      }
      return;
    }
    const fallbackVariation = nextStationVariation(activeStation, nextRound(activeStation.id), userSettings.defaultDurationSeconds);
    const track = await generateTrack(fallbackVariation.prompt, fallbackVariation.settings, activeStation.id);
    if (track) {
      void queueNextStationTrack(activeStation);
    }
  }

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    try {
      if (audio.paused) {
        setShouldAutoplay(true);
        await audio.play();
        setIsAudioPlaying(true);
      } else {
        audio.pause();
        setIsAudioPlaying(false);
      }
    } catch {
      setNotice("Press play once and the station will keep going from there.");
    }
  }

  async function playTrack(track: Track) {
    setContinuousMode(false);
    setPromptStationSource("");
    setQueuedTrack(null);
    setShouldAutoplay(true);
    if (track.trackId === currentTrackId) {
      await togglePlayback();
      return;
    }
    setCurrentTrackId(track.trackId);
  }

  function handleContinuousChange(checked: boolean) {
    setContinuousMode(checked);
    if (!checked) {
      setQueuedTrack(null);
      return;
    }
    if (currentTrack && !queuedTrack) {
      if (activeStationId === PROMPT_STATION_ID) {
        void queueNextPromptStationTrack(promptStationSource || currentTrack.arrangement?.sourcePrompt || prompt, true);
        return;
      }
      void queueNextStationTrack(activeStation, true);
    }
  }

  function showStations() {
    setActiveView("stations");
    requestAnimationFrame(() => {
      document.getElementById("stations")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function toggleFavorite(track: Track) {
    try {
      const payload = await api<{ track: Track }>(`/tracks/${track.trackId}/favorite`, {
        method: "POST",
        body: JSON.stringify({ favorite: !track.favorite })
      });
      setTracks((items) => items.map((item) => (item.trackId === track.trackId ? payload.track : item)));
    } catch (favoriteError) {
      setError(favoriteError instanceof Error ? favoriteError.message : "Favorite update failed.");
    }
  }

  async function createPlaylist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = playlistName.trim();
    if (!name) return;
    try {
      const payload = await api<{ playlist: Playlist }>("/playlists", {
        method: "POST",
        body: JSON.stringify({ name })
      });
      setPlaylists((items) => [payload.playlist, ...items]);
      setSelectedPlaylistId(payload.playlist.playlistId);
      setPlaylistName("");
      setNotice("Playlist created.");
    } catch (playlistError) {
      setError(playlistError instanceof Error ? playlistError.message : "Playlist creation failed.");
    }
  }

  async function addTrackToPlaylist(track: Track) {
    if (!selectedPlaylistId) return;
    try {
      const payload = await api<{ playlist: Playlist }>(`/playlists/${selectedPlaylistId}/tracks`, {
        method: "POST",
        body: JSON.stringify({ trackId: track.trackId })
      });
      setPlaylists((items) => items.map((item) => (item.playlistId === payload.playlist.playlistId ? payload.playlist : item)));
      setNotice("Added to playlist.");
    } catch (playlistError) {
      setError(playlistError instanceof Error ? playlistError.message : "Could not add track to playlist.");
    }
  }

  async function saveAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const previousUserId = user?.userId || "";
    setIsSavingAccount(true);
    setError("");
    setNotice("");
    try {
      const payload = await api<{ user: ByteBistroUser }>("/auth/demo", {
        method: "POST",
        body: JSON.stringify({ name: accountDraft.name, email: accountDraft.email })
      });
      setUser(payload.user);
      storeUser(payload.user);
      setAccountDraft({ name: payload.user.displayName, email: payload.user.email, password: "" });
      if (payload.user.userId !== previousUserId) {
        setQueuedTrack(null);
        setIsAudioPlaying(false);
        await Promise.all([loadTracks(true), loadPlaylists(true), loadUserSettings(true)]);
      }
      setNotice(payload.user.email ? "Signed in and synced." : "Account settings saved.");
    } catch (accountError) {
      setError(accountError instanceof Error ? accountError.message : "Account update failed.");
    } finally {
      setIsSavingAccount(false);
    }
  }

  async function signupAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const previousUserId = user?.userId || "";
    setIsSavingAccount(true);
    setError("");
    setNotice("");
    try {
      const payload = await api<{ user: ByteBistroUser; settings: UserSettings }>("/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          name: accountDraft.name,
          email: accountDraft.email,
          password: accountDraft.password
        })
      });
      setUser(payload.user);
      storeUser(payload.user);
      setAccountDraft({ name: payload.user.displayName, email: payload.user.email, password: "" });
      if (payload.settings) {
        applySavedSettings(payload.settings);
      }
      if (payload.user.userId !== previousUserId) {
        setQueuedTrack(null);
        setIsAudioPlaying(false);
        await Promise.all([loadTracks(true), loadPlaylists(true)]);
      }
      setNotice("Account ready. Library and settings are synced.");
    } catch (signupError) {
      setError(signupError instanceof Error ? signupError.message : "Signup failed.");
    } finally {
      setIsSavingAccount(false);
    }
  }

  async function saveUserSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSavingAccount(true);
    setError("");
    setNotice("");
    try {
      const payload = await api<{ settings: UserSettings }>("/settings", {
        method: "PATCH",
        body: JSON.stringify(settingsDraft)
      });
      applySavedSettings(payload.settings);
      setNotice("Settings saved.");
    } catch (settingsError) {
      setError(settingsError instanceof Error ? settingsError.message : "Settings update failed.");
    } finally {
      setIsSavingAccount(false);
    }
  }

  async function signOut() {
    setIsSavingAccount(true);
    setError("");
    setNotice("");
    try {
      await api<Record<string, never>>("/auth/logout", { method: "POST", body: JSON.stringify({}) });
      clearStoredUser();
      const auth = await api<{ user: ByteBistroUser }>("/auth/demo", {
        method: "POST",
        body: JSON.stringify({ name: "The Byte Bistro Listener" })
      });
      setUser(auth.user);
      storeUser(auth.user);
      setAccountDraft({ name: auth.user.displayName, email: auth.user.email, password: "" });
      setQueuedTrack(null);
      setIsAudioPlaying(false);
      await Promise.all([loadTracks(true), loadPlaylists(true), loadUserSettings(true)]);
      setNotice("Signed out. New guest session ready.");
    } catch (logoutError) {
      setError(logoutError instanceof Error ? logoutError.message : "Could not sign out.");
    } finally {
      setIsSavingAccount(false);
    }
  }

  return (
    <div className="appShell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="The Byte Bistro home">
          <LogoMark />
          <span className="brandWords">
            <span className="brandKicker">The</span>
            <strong>Byte Bistro</strong>
            <small>AI instrumental radio</small>
          </span>
        </a>

        <nav className="navTabs" aria-label="The Byte Bistro sections">
          <NavButton icon={<Radio size={17} />} label="Stations" active={activeView === "stations"} onClick={() => setActiveView("stations")} />
          <NavButton icon={<Library size={17} />} label="Library" active={activeView === "library"} onClick={() => setActiveView("library")} />
          <NavButton icon={<Heart size={17} />} label="Favorites" active={activeView === "favorites"} onClick={() => setActiveView("favorites")} />
          <NavButton icon={<Settings size={17} />} label="Account" active={activeView === "account"} onClick={() => setActiveView("account")} />
        </nav>

        <button className="accountChip" type="button" onClick={() => setActiveView("account")}>
          <User size={16} />
          <span>{user?.displayName || "Listener"}</span>
        </button>
      </header>

      <main id="top">
        <section className="hero">
          <img className="heroImage" src="/static/thebytebistro/studio-hero.png" alt="Studio desk with laptop waveforms, MIDI keyboard, headphones, and warm lighting" />
          <div className="heroShade" />
          <div className="heroContent">
            <div className="heroBrandLockup">
              <LogoMark />
              <span>The Byte Bistro</span>
            </div>
            <p className="eyebrow">No vocals. No covers. Just instrumental ambience.</p>
            <h1>Pick a genre and let it play.</h1>
            <p className="heroLead">
              The Byte Bistro turns prompts into original background music, but the default experience is simpler:
              tune into a station and vibe.
            </p>
            <div className="heroActions">
              <button className="primaryButton heroButton" type="button" onClick={() => tuneStation(defaultStation)} disabled={isGenerating}>
                {isGenerating ? <Loader2 className="spin" size={18} /> : <Play size={18} fill="currentColor" />}
                <span>{isGenerating ? "Tuning" : "Start LoFi Study"}</span>
              </button>
              <button className="ghostButton heroButton" type="button" onClick={showStations}>
                <Waves size={18} />
                <span>Browse stations</span>
              </button>
            </div>
          </div>
        </section>

        <section className="playerDock" aria-label="Now streaming">
          <div className="playerMeta">
            <span className="liveDot" />
            <div>
              <p className="eyebrow">Now streaming</p>
              <h2>{currentTrack?.title || "Choose a station"}</h2>
              <p>
                {currentTrack
                  ? `${currentTrack.styleLabel} · ${currentTrack.bpm} BPM · ${formatDuration(currentTrack.durationSeconds)}`
                  : "A freshly generated instrumental will appear here."}
              </p>
            </div>
          </div>
          <div className="playerControls">
            <button
              className="roundButton"
              type="button"
              disabled={!currentTrack}
              onClick={togglePlayback}
              title={isAudioPlaying ? "Pause track" : "Play track"}
              aria-label={isAudioPlaying ? "Pause track" : "Play track"}
            >
              {isAudioPlaying ? <Pause size={18} /> : <Play size={18} fill="currentColor" />}
            </button>
            {currentTrack ? (
              <>
                <audio
                  ref={audioRef}
                  controls
                  src={fullUrl(currentTrack.audioUrl)}
                  aria-label={`Play ${currentTrack.title}`}
                  onEnded={() => void handleTrackEnded()}
                  onPlay={() => {
                    setShouldAutoplay(true);
                    setIsAudioPlaying(true);
                  }}
                  onPause={() => setIsAudioPlaying(false)}
                />
                <button
                  className="iconTextButton secondaryButton"
                  type="button"
                  onClick={handleTrackEnded}
                  disabled={!continuousMode || (!queuedTrack && isQueueing)}
                >
                  <SkipForward size={17} />
                  <span>Next</span>
                </button>
                <a className="iconTextButton" href={fullUrl(currentTrack.downloadUrl)}>
                  <ArrowDownToLine size={17} />
                  <span>MP3</span>
                </a>
              </>
            ) : (
              <div className="audioPlaceholder">No track tuned yet</div>
            )}
          </div>
          <div className="streamOptions">
            <label className="streamToggle">
              <input
                type="checkbox"
                checked={continuousMode}
                onChange={(event) => handleContinuousChange(event.target.checked)}
              />
              <span>Continuous station</span>
            </label>
            <span className="queueStatus">
              {isQueueing ? "Creating up next" : queuedTrack ? `Up next: ${queuedTrack.title}` : "Queue starts after you tune in"}
            </span>
          </div>
        </section>

        <section className="workspace">
          <div className="statusRow">
            {isLoading ? <Status tone="info" text="Loading stations." /> : null}
            {notice ? <Status tone="success" text={notice} onDismiss={() => setNotice("")} /> : null}
            {error ? <Status tone="danger" text={error} onDismiss={() => setError("")} /> : null}
          </div>

          {activeView === "account" ? (
            <AccountView
              user={user}
              draft={accountDraft}
              setDraft={setAccountDraft}
              signupAccount={signupAccount}
              signOut={signOut}
              settingsDraft={settingsDraft}
              setSettingsDraft={setSettingsDraft}
              saveUserSettings={saveUserSettings}
              isSaving={isSavingAccount}
              trackCount={tracks.length}
              playlistCount={playlists.length}
            />
          ) : (
            <>
              <section className="sectionBlock" id="stations">
                <div className="sectionHeader">
                  <div>
                    <p className="eyebrow">Streaming by genre</p>
                    <h2>Stations</h2>
                  </div>
                  <span className="countBadge">{stations.length}</span>
                </div>

                <div className="stationGrid">
                  {stations.map((station) => (
                    <StationCard
                      key={station.id}
                      station={station}
                      isActive={activeStationId === station.id}
                      isGenerating={isGenerating && activeStationId === station.id}
                      onTune={() => tuneStation(station)}
                    />
                  ))}
                </div>
              </section>

              <div className="contentGrid">
                <section className="sectionBlock customStation">
                  <div className="sectionHeader">
                    <div>
                      <p className="eyebrow">Make a station</p>
                      <h2>AI station</h2>
                    </div>
                    <Sparkles size={22} />
                  </div>

                  <form className="mixerForm" onSubmit={generateCustom}>
                    <label className="fieldStack" htmlFor="prompt">
                      <span>Prompt</span>
                      <textarea
                        id="prompt"
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        rows={4}
                        maxLength={420}
                        placeholder="rainy coding focus music with piano, soft drums, and a warm late-night feel"
                      />
                    </label>

                    <div className={arrangedTrack ? "arrangerPanel" : "arrangerPanel idle"}>
                      <div className="arrangerTopline">
                        <span>AI arrangement</span>
                        <strong>{arrangedTrack?.title || "Ready for a prompt"}</strong>
                      </div>
                      <div className="arrangerStats">
                        <span>{arrangedTrack?.styleLabel || "Instrumental radio"}</span>
                        <span>{arrangedTrack ? `${arrangedTrack.bpm} BPM` : formatDuration(userSettings.defaultDurationSeconds)}</span>
                        <span>{arrangedTrack ? arrangedTrack.key : "MP3"}</span>
                      </div>
                      <div className="instrumentRow" aria-label="Generated instruments">
                        {(arrangedTrack?.instruments || settings.instruments).map((instrument) => (
                          <span key={instrument}>{formatInstrumentLabel(instrument)}</span>
                        ))}
                      </div>
                    </div>

                    <button className="primaryButton" type="submit" disabled={isGenerating || prompt.trim().length < 4}>
                      {isGenerating ? <Loader2 className="spin" size={18} /> : <Disc3 size={18} />}
                      <span>{isGenerating ? "Creating station" : "Create station"}</span>
                    </button>
                  </form>
                </section>

                <aside className="sectionBlock playlistPanel">
                  <div className="sectionHeader">
                    <div>
                      <p className="eyebrow">Sets</p>
                      <h2>Playlists</h2>
                    </div>
                    <ListMusic size={22} />
                  </div>

                  <form className="playlistForm" onSubmit={createPlaylist}>
                    <input
                      value={playlistName}
                      onChange={(event) => setPlaylistName(event.target.value)}
                      placeholder="Playlist name"
                      aria-label="Playlist name"
                    />
                    <button type="submit" title="Create playlist" aria-label="Create playlist">
                      <Plus size={18} />
                    </button>
                  </form>

                  <div className="playlistList">
                    {playlists.map((playlist) => (
                      <button
                        key={playlist.playlistId}
                        type="button"
                        className={selectedPlaylistId === playlist.playlistId ? "playlistItem selected" : "playlistItem"}
                        onClick={() => setSelectedPlaylistId(playlist.playlistId)}
                      >
                        <span>{playlist.name}</span>
                        <small>{playlist.trackIds.length} tracks</small>
                      </button>
                    ))}
                  </div>
                  {!playlists.length ? <p className="quietText">Save the tracks you want to come back to.</p> : null}
                  {selectedPlaylist ? <p className="quietText">Selected: {selectedPlaylist.name}</p> : null}
                </aside>
              </div>

              <section className="sectionBlock libraryBlock">
                <div className="sectionHeader libraryHeader">
                  <div>
                    <p className="eyebrow">{activeView === "favorites" ? "Saved vibes" : "Recently generated"}</p>
                    <h2>{activeView === "favorites" ? "Favorites" : "Library"}</h2>
                  </div>
                  <label className="searchBox" htmlFor="trackSearch">
                    <Search size={17} />
                    <input
                      id="trackSearch"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="Search tracks"
                    />
                  </label>
                </div>

                {visibleTracks.length ? (
                  <div className="trackList">
                    {visibleTracks.map((track) => (
                      <TrackCard
                        key={track.trackId}
                        track={track}
                        isCurrent={currentTrack?.trackId === track.trackId}
                        selectedPlaylistId={selectedPlaylistId}
                        isPlaying={isAudioPlaying && currentTrack?.trackId === track.trackId}
                        onPlay={() => void playTrack(track)}
                        onFavorite={() => toggleFavorite(track)}
                        onAddToPlaylist={() => addTrackToPlaylist(track)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="emptyState">
                    <Music2 size={34} />
                    <h3>No tracks yet</h3>
                    <p>Tune a station and The Byte Bistro will build your first instrumental.</p>
                  </div>
                )}
              </section>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function LogoMark() {
  return (
    <span className="brandMark" aria-hidden="true">
      <Coffee size={20} strokeWidth={2.6} />
      <span className="brandWaveBars">
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

function NavButton({
  icon,
  label,
  active,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={active ? "navButton active" : "navButton"} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function StationCard({
  station,
  isActive,
  isGenerating,
  onTune
}: {
  station: Station;
  isActive: boolean;
  isGenerating: boolean;
  onTune: () => void;
}) {
  return (
    <article className={`stationCard ${station.accent} ${isActive ? "active" : ""}`}>
      <div className="stationTopline">
        <span>{station.genre}</span>
        <Radio size={17} />
      </div>
      <h3>{station.title}</h3>
      <p>{station.vibe}</p>
      <div className="stationTags">
        {station.tags.map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
      <button className="stationButton" type="button" onClick={onTune} disabled={isGenerating}>
        {isGenerating ? <Loader2 className="spin" size={17} /> : <Play size={17} fill="currentColor" />}
        <span>{isActive ? "Tune again" : "Tune in"}</span>
      </button>
    </article>
  );
}

function Status({ tone, text, onDismiss }: { tone: "info" | "success" | "danger"; text: string; onDismiss?: () => void }) {
  return (
    <div className={`status ${tone}`}>
      <span>{text}</span>
      {onDismiss ? (
        <button type="button" onClick={onDismiss} aria-label="Dismiss message">
          x
        </button>
      ) : null}
    </div>
  );
}

function TrackCard({
  track,
  isCurrent,
  isPlaying,
  selectedPlaylistId,
  onPlay,
  onFavorite,
  onAddToPlaylist
}: {
  track: Track;
  isCurrent: boolean;
  isPlaying: boolean;
  selectedPlaylistId: string;
  onPlay: () => void;
  onFavorite: () => void;
  onAddToPlaylist: () => void;
}) {
  return (
    <article className={isCurrent ? "trackCard current" : "trackCard"}>
      <button
        className="roundButton playSmall"
        type="button"
        onClick={onPlay}
        title={isPlaying ? "Pause track" : "Play track"}
        aria-label={`${isPlaying ? "Pause" : "Play"} ${track.title}`}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="trackCopy">
        <h3>{track.title}</h3>
        <p>{track.prompt}</p>
        <div className="trackMeta">
          <span>{track.styleLabel}</span>
          <span>{track.bpm} BPM</span>
          <span>{track.key}</span>
          <span>{formatDuration(track.durationSeconds)}</span>
        </div>
      </div>
      <div className="trackActions">
        <button className={track.favorite ? "iconButton favorited" : "iconButton"} type="button" onClick={onFavorite} title="Toggle favorite" aria-label="Toggle favorite">
          <Heart size={18} fill={track.favorite ? "currentColor" : "none"} />
        </button>
        <a className="iconButton" href={fullUrl(track.downloadUrl)} title="Download MP3" aria-label={`Download ${track.title}`}>
          <ArrowDownToLine size={18} />
        </a>
        <button className="iconButton" type="button" onClick={onAddToPlaylist} disabled={!selectedPlaylistId} title="Add to selected playlist" aria-label="Add to selected playlist">
          <ListMusic size={18} />
        </button>
      </div>
    </article>
  );
}

function AccountView({
  user,
  draft,
  setDraft,
  signupAccount,
  signOut,
  settingsDraft,
  setSettingsDraft,
  saveUserSettings,
  isSaving,
  trackCount,
  playlistCount
}: {
  user: ByteBistroUser | null;
  draft: { name: string; email: string; password: string };
  setDraft: (draft: { name: string; email: string; password: string }) => void;
  signupAccount: (event: FormEvent<HTMLFormElement>) => void;
  signOut: () => void;
  settingsDraft: UserSettings;
  setSettingsDraft: (settings: UserSettings) => void;
  saveUserSettings: (event: FormEvent<HTMLFormElement>) => void;
  isSaving: boolean;
  trackCount: number;
  playlistCount: number;
}) {
  return (
    <div className="accountGrid">
      <form className="sectionBlock accountPanel" onSubmit={signupAccount}>
        <div className="sectionHeader">
          <div>
            <p className="eyebrow">Signup</p>
            <h2>Create your account</h2>
          </div>
          <ShieldCheck size={22} />
        </div>
        <p className="quietText">
          {user?.isGuest
            ? "Save your library and station preferences with an email and password."
            : `Signed in as ${user?.email || "a saved listener"}.`}
        </p>
        <label className="fieldStack" htmlFor="displayName">
          <span>Name</span>
          <input
            id="displayName"
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          />
        </label>
        <label className="fieldStack" htmlFor="email">
          <span>Email</span>
          <input
            id="email"
            type="email"
            value={draft.email}
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
          />
        </label>
        <label className="fieldStack" htmlFor="password">
          <span>Password</span>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={draft.password}
            onChange={(event) => setDraft({ ...draft, password: event.target.value })}
          />
        </label>
        <div className="accountActions">
          <button className="primaryButton" type="submit" disabled={isSaving || draft.password.length < 8}>
            {isSaving ? <Loader2 className="spin" size={18} /> : <ShieldCheck size={18} />}
            <span>{isSaving ? "Saving" : "Create / sign in"}</span>
          </button>
          <button className="ghostButton" type="button" onClick={signOut} disabled={isSaving}>
            <LogOut size={18} />
            <span>New guest</span>
          </button>
        </div>
      </form>

      <form className="sectionBlock accountPanel" onSubmit={saveUserSettings}>
        <div className="sectionHeader">
          <div>
            <p className="eyebrow">Settings</p>
            <h2>Station defaults</h2>
          </div>
          <SlidersHorizontal size={22} />
        </div>
        <label className="fieldStack" htmlFor="defaultStation">
          <span>Default station</span>
          <select
            id="defaultStation"
            value={settingsDraft.defaultStationId}
            onChange={(event) => setSettingsDraft({ ...settingsDraft, defaultStationId: event.target.value })}
          >
            {stations.map((station) => (
              <option key={station.id} value={station.id}>
                {station.title}
              </option>
            ))}
          </select>
        </label>
        <label className="fieldStack" htmlFor="defaultDuration">
          <span>Default track length: {formatDuration(settingsDraft.defaultDurationSeconds)}</span>
          <input
            id="defaultDuration"
            type="range"
            min={180}
            max={600}
            step={30}
            value={settingsDraft.defaultDurationSeconds}
            onChange={(event) => setSettingsDraft({ ...settingsDraft, defaultDurationSeconds: Number(event.target.value) })}
          />
        </label>
        <label className="fieldStack" htmlFor="downloadQuality">
          <span>Download quality</span>
          <select
            id="downloadQuality"
            value={settingsDraft.downloadQuality}
            onChange={(event) =>
              setSettingsDraft({ ...settingsDraft, downloadQuality: event.target.value === "high" ? "high" : "standard" })
            }
          >
            <option value="standard">Standard MP3</option>
            <option value="high">High quality MP3</option>
          </select>
        </label>
        <div className="settingsToggles">
          <label className="toggleRow">
            <input
              type="checkbox"
              checked={settingsDraft.continuousStation}
              onChange={(event) => setSettingsDraft({ ...settingsDraft, continuousStation: event.target.checked })}
            />
            <span>Keep stations running</span>
          </label>
          <label className="toggleRow">
            <input
              type="checkbox"
              checked={settingsDraft.autoPlayOnTune}
              onChange={(event) => setSettingsDraft({ ...settingsDraft, autoPlayOnTune: event.target.checked })}
            />
            <span>Auto-play after tuning</span>
          </label>
          <label className="toggleRow">
            <input
              type="checkbox"
              checked={settingsDraft.emailUpdates}
              onChange={(event) => setSettingsDraft({ ...settingsDraft, emailUpdates: event.target.checked })}
            />
            <span>Email product updates</span>
          </label>
        </div>
        <button className="primaryButton" type="submit" disabled={isSaving}>
          {isSaving ? <Loader2 className="spin" size={18} /> : <Settings size={18} />}
          <span>{isSaving ? "Saving" : "Save settings"}</span>
        </button>
      </form>

      <section className="sectionBlock accountPanel">
        <div className="sectionHeader">
          <div>
            <p className="eyebrow">The Byte Bistro</p>
            <h2>{user?.plan || "studio-preview"}</h2>
          </div>
          <Disc3 size={22} />
        </div>
        <div className="logoPreview">
          <LogoMark />
          <div>
            <span>The</span>
            <strong>Byte Bistro</strong>
            <small>Instrumental stations</small>
          </div>
        </div>
        <div className="metricGrid">
          <div>
            <strong>{trackCount}</strong>
            <span>Tracks</span>
          </div>
          <div>
            <strong>{playlistCount}</strong>
            <span>Playlists</span>
          </div>
        </div>
        <p className="quietText">User ID: {user?.userId || "pending"}</p>
      </section>
    </div>
  );
}

export default App;
